import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { choices, chosenValue, useAudioDevices } from '../lib/audioDevices';
import {
  cameraTrack,
  chooseAudio,
  deskPhoneOf,
  dismissNotice,
  endPatch,
  flipCamera,
  leavePatch,
  leaveVoicemail,
  openPanel,
  patchNow,
  screenTrack,
  setExpanded,
  startSound,
  toggleCamera,
  toggleMute,
  toggleShare,
  usePatch,
  useTicking,
  watchScreen,
  type PatchPerson,
  type PatchState
} from '../lib/patch';
import { closePip, offerAutoPip, openPip, pipSupported, usePipWindow } from '../lib/patchPip';
import { cornerToward, galleryLayout, nearestCorner, saveCorner, savedCorner, stripTiles, tilesThatFit, type Corner, type StripTile } from '../lib/patchStrip';
import { clockText, namesText, statusText } from '../lib/patchText';
import { Button, Notice } from './ui';
import { Face } from './PhotoEditor';

/**
 * The Patch someone is in, wherever they are in the app: a small floating bar ("Patched in with
 * Sam · 4:12", mute, leave), opened into a panel with who's in (and who's speaking), cameras, the
 * microphone, sharing a screen and watching one. The panel can fill the window (Full screen), with
 * a shared screen as the stage; while someone's video is on and the panel isn't open, a film strip
 * of the video floats in a corner instead of the bar, and can pop out into a window of its own
 * (lib/patchPip). On a phone: a sheet from the bottom, the whole screen once anyone's camera is on.
 * Marked private, so live help and session recordings leave it out.
 */

/** The Patch is a sheet from the bottom of the screen here (a phone): no film strip. */
const PHONE = '(max-width: 560px)';
/** The menu is across the top and the tab bar along the bottom: the strip sits where the bar does. */
const NARROW = '(max-width: 860px)';

function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (l) => {
      const m = window.matchMedia(query);
      m.addEventListener?.('change', l);
      return () => m.removeEventListener?.('change', l);
    },
    () => window.matchMedia(query).matches,
    () => false
  );
}

/** The element the browser is showing full screen (the panel or a shared screen), if any. */
function useFullscreenElement(): Element | null {
  return useSyncExternalStore(
    (l) => {
      document.addEventListener('fullscreenchange', l);
      return () => document.removeEventListener('fullscreenchange', l);
    },
    () => document.fullscreenElement,
    () => null
  );
}

const exitFullscreen = () => {
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
};

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const live = (s: PatchState) => s.phase === 'joining' || s.phase === 'connected' || s.phase === 'reconnecting';

/** Someone's camera or a shared screen is on: there's video to show in the film strip. */
const hasVideo = (s: PatchState) => s.videos.length > 0 || s.screens.length > 0;

export function PatchDock() {
  const s = usePatch();
  const { state: auth } = useAuth();
  const signedOut = Boolean(auth && !auth.signedIn);
  const pip = usePipWindow();
  const phone = useMedia(PHONE);
  const inPatch = live(s);
  useEffect(() => {
    if (signedOut) void leavePatch();
  }, [signedOut]);
  // During the Patch the browser may open its window itself when the person goes to another tab;
  // the window closes when the Patch ends.
  useEffect(() => {
    if (!inPatch) {
      closePip();
      return;
    }
    offerAutoPip(true);
    return () => offerAutoPip(false);
  }, [inPatch]);
  // Going to another page with video on: the panel makes way for the film strip (not on a phone,
  // where the Patch is the whole screen while video is on).
  const { pathname } = useLocation();
  const seen = useRef(pathname);
  useEffect(() => {
    if (seen.current === pathname) return;
    seen.current = pathname;
    const now = patchNow();
    if (now.open && live(now) && hasVideo(now) && !window.matchMedia(PHONE).matches) openPanel(false);
  }, [pathname]);
  if (s.phase === 'idle' || signedOut) return null;
  if (s.phase === 'ended') return <PatchEnded notice={s.notice} />;
  return (
    <>
      {s.open ? <PatchPanel s={s} /> : hasVideo(s) && !phone && !pip ? <FilmStrip s={s} /> : <PatchBar s={s} />}
      {pip && createPortal(<FilmStrip s={s} pip={pip} />, pip.document.body)}
    </>
  );
}

function statusOf(s: PatchState, now: number): string {
  if (s.phase === 'joining') return 'Joining the Patch…';
  if (s.phase === 'reconnecting') return 'Reconnecting…';
  const others = s.people.filter((p) => !p.me).map((p) => p.name);
  return statusText(others, (now - s.startedAt) / 1000);
}

function MuteButton({ s, small }: { s: PatchState; small?: boolean }) {
  const off = s.muted || Boolean(s.micProblem);
  return (
    <Button
      size={small ? 'small' : undefined}
      variant={off ? 'secondary' : 'ghost'}
      icon={off ? 'micOff' : 'mic'}
      aria-pressed={off}
      disabled={(s.phase !== 'connected' && s.phase !== 'reconnecting') || s.micBusy}
      onClick={() => void toggleMute()}
      className="patch-mute"
    >
      <span className="patch-btn-label">{off ? 'Unmute' : 'Mute'}</span>
    </Button>
  );
}

function PatchBar({ s }: { s: PatchState }) {
  const now = useTicking(s.phase === 'connected');
  const status = statusOf(s, now);
  const speaking = s.people.some((p) => p.speaking && !p.me);
  return (
    <div className="patch-bar" role="region" aria-label="Patch" data-private="">
      <button type="button" className="patch-bar-main" onClick={() => openPanel(true)} title="Open the Patch">
        <span className={`patch-dot${speaking ? ' speaking' : ''}${s.phase !== 'connected' ? ' waiting' : ''}`} aria-hidden="true" />
        <span className="patch-bar-text">
          <strong>{status}</strong>
          <span>{s.title}</span>
        </span>
        <Icon name="expand" size={15} />
      </button>
      <MuteButton s={s} small />
      <Button size="small" variant={s.starter ? 'secondary' : 'danger'} icon="logout" onClick={() => void leavePatch()} title={s.starter ? 'Leave: the Patch carries on' : undefined}>
        Leave
      </Button>
      {s.starter && (
        <Button size="small" variant="danger" icon="x" onClick={() => void endPatch()} title="End the Patch for everyone">
          End
        </Button>
      )}
    </div>
  );
}

/** Turns this person's camera on or off; on a phone, switches between the front and back ones. */
function CameraButtons({ s }: { s: PatchState }) {
  const live = s.phase === 'connected' || s.phase === 'reconnecting';
  return (
    <>
      <Button variant={s.camera ? 'secondary' : 'ghost'} icon={s.camera ? 'videoOff' : 'video'} aria-pressed={s.camera} disabled={!live} onClick={() => void toggleCamera()} className="patch-camera">
        <span className="patch-btn-label">{s.camera ? 'Stop video' : 'Video'}</span>
      </Button>
      {s.camera && s.canFlip && (
        <Button variant="ghost" icon="flipCamera" aria-label="Switch camera" title="Switch camera" disabled={!live} onClick={() => void flipCamera()} className="patch-flip" />
      )}
    </>
  );
}

/**
 * One person: their camera when it's on, else their photo or initials (ringed while they speak).
 * `inset`: this person's own, small in the corner of the others' tiles.
 */
function Tile({ person, video, mirrored, inset, at }: { person: PatchPerson; video: boolean; mirrored?: boolean; inset?: boolean; at?: CSSProperties }) {
  const ref = useRef<HTMLVideoElement>(null);
  const track = video ? cameraTrack(person.id) : null;
  useEffect(() => {
    const el = ref.current;
    if (!track || !el) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track]);
  const label = `${person.name}${person.me ? ' (you)' : ''}${person.speaking ? ', speaking' : ''}${person.muted ? ', muted' : ''}`;
  return (
    <figure className={`patch-tile${person.me && inset ? ' self' : ''}${person.speaking ? ' speaking' : ''}${track ? '' : ' no-video'}`} aria-label={label} style={at}>
      {track ? (
        <video ref={ref} autoPlay playsInline muted className={mirrored ? 'mirror' : undefined} />
      ) : (
        <Face userId={faceOf(person.id)} name={person.name} size="medium" className="patch-tile-avatar" />
      )}
      <figcaption aria-hidden="true">
        <span>
          {person.name}
          {person.me && ' (you)'}
        </span>
        {person.muted && <Icon name="micOff" size={13} />}
      </figcaption>
    </figure>
  );
}

/** An element's size, kept up to date (while `on`). */
function useSize(ref: RefObject<HTMLElement | null>, on: boolean): { w: number; h: number } | null {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!on || !el) {
      setSize(null);
      return;
    }
    const measure = () => setSize((was) => (was && was.w === el.clientWidth && was.h === el.clientHeight ? was : { w: el.clientWidth, h: el.clientHeight }));
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, [ref, on]);
  return size;
}

/**
 * Everyone as a tile, once anyone's camera is on (and always in a full-screen Patch): the others
 * large, this person small in a corner. Full screen, the tiles are as large as fit the window.
 */
function Tiles({ s, full }: { s: PatchState; full?: boolean }) {
  const others = s.people.filter((p) => !p.me);
  const me = s.people.find((p) => p.me);
  const on = (p: PatchPerson) => s.videos.some((v) => v.id === p.id);
  const box = useRef<HTMLDivElement>(null);
  const size = useSize(box, Boolean(full));
  const fit = full && size && others.length ? galleryLayout(others.length, size.w, size.h) : null;
  // Full screen, this person's own picture sits in the corner of the others' tiles, not of the window.
  const corner: CSSProperties | undefined =
    fit && size
      ? {
          right: Math.max(14, (size.w - (fit.cols * fit.width + (fit.cols - 1) * 8)) / 2 + 12),
          bottom: Math.max(14, (size.h - (fit.rows * fit.height + (fit.rows - 1) * 8)) / 2 + 12)
        }
      : undefined;
  return (
    <div
      ref={box}
      className={`patch-tiles n${Math.min(Math.max(others.length, 1), 4)}${full ? ' gallery' : ''}`}
      style={fit ? { gridTemplateColumns: `repeat(${fit.cols}, ${fit.width}px)`, gridAutoRows: `${fit.height}px` } : undefined}
    >
      {others.map((p) => (
        <Tile key={p.id} person={p} video={on(p)} />
      ))}
      {!others.length && <p className="patch-alone">Waiting for others to join…</p>}
      {me && <Tile person={me} video={on(me)} mirrored={s.facing === 'user'} inset at={corner} />}
    </div>
  );
}

/** Beside (or under) a shared screen on the stage: everyone, this person last. */
function StageStrip({ s }: { s: PatchState }) {
  const on = (p: PatchPerson) => s.videos.some((v) => v.id === p.id);
  const people = [...s.people.filter((p) => !p.me), ...s.people.filter((p) => p.me)];
  return (
    <div className="patch-stage-strip" role="group" aria-label="Who’s in">
      {people.map((p) => (
        <Tile key={p.id} person={p} video={on(p)} mirrored={p.me && s.facing === 'user'} />
      ))}
    </div>
  );
}

function PatchPanel({ s }: { s: PatchState }) {
  const now = useTicking(s.phase === 'connected');
  const navigate = useNavigate();
  const panel = useRef<HTMLElement>(null);
  const fullscreen = useFullscreenElement();
  const pip = usePipWindow();
  const watching = s.watching ? s.screens.find((x) => x.id === s.watching) : undefined;
  const video = s.videos.length > 0;
  const full = s.expanded;
  const browserFull = Boolean(fullscreen && fullscreen === panel.current);
  // Esc leaves full screen. (While the browser itself is full screen, Esc leaves that first.)
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.fullscreenElement) return;
      if (e.target instanceof HTMLSelectElement) return;
      setExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [full]);
  const toggleFull = () => {
    if (full) exitFullscreen();
    setExpanded(!full);
  };
  const toggleBrowser = () => {
    if (document.fullscreenElement) return exitFullscreen();
    void panel.current?.requestFullscreen?.().catch(() => undefined);
  };
  const makeSmall = () => {
    exitFullscreen();
    openPanel(false);
  };
  const toConversation = () => {
    navigate(`/t/${encodeURIComponent(s.tenant)}/messages/${encodeURIComponent(s.conversationId)}`);
    // Full screen covers the conversation: it goes to the film strip (or the bar) until opened again.
    if (full) makeSmall();
  };
  const tiles = (watching ? 1 : 0) + Math.min(s.people.length, 3);
  return (
    <section
      ref={panel}
      className={`patch-panel${full ? ' full' : watching || video ? ' wide' : ''}${video ? ' with-video' : ''}`}
      aria-label={`Patch: ${s.title}`}
      data-private=""
    >
      <header className="patch-head">
        <span className={`patch-dot${s.phase !== 'connected' ? ' waiting' : ''}`} aria-hidden="true" />
        <div className="patch-head-text">
          <h2>{s.title}</h2>
          <span>{statusOf(s, now)}</span>
        </div>
        {pipSupported() && !pip && (full || hasVideo(s)) && (
          <button
            type="button"
            className="icon-btn patch-pop"
            aria-label="Pop out"
            title="Pop out: the Patch in a small window of its own, over your other tabs and apps"
            onClick={() => openPip(96 + 154 * Math.min(tiles, 3))}
          >
            <Icon name="popOut" size={18} />
          </button>
        )}
        {full && document.fullscreenEnabled && (
          <button
            type="button"
            className="icon-btn"
            aria-label={browserFull ? 'Show browser' : 'Hide browser'}
            title={browserFull ? 'Show your browser’s tabs and bars again (Esc)' : 'Hide your browser’s tabs and bars: the Patch takes your whole display'}
            onClick={toggleBrowser}
          >
            <Icon name="fullscreen" size={18} />
          </button>
        )}
        <button type="button" className="icon-btn" aria-label="Go to the conversation" title="Go to the conversation" onClick={toConversation}>
          <Icon name="chat" size={18} />
        </button>
        <button type="button" className="icon-btn patch-full-btn" aria-label={full ? 'Exit full screen' : 'Full screen'} title={full ? 'Exit full screen (Esc)' : 'Full screen'} onClick={toggleFull}>
          <Icon name={full ? 'shrink' : 'expand'} size={18} />
        </button>
        <button type="button" className="icon-btn" aria-label="Make the Patch small" title="Make small" onClick={makeSmall}>
          <Icon name="chev" size={18} />
        </button>
      </header>
      <div className="patch-body">
        {s.needsClick && (
          <Notice tone="warn" title="Your browser is holding the sound back">
            <Button size="small" variant="primary" onClick={() => void startSound()}>
              Turn on sound
            </Button>
          </Notice>
        )}
        {s.micProblem && <Notice tone="warn">{s.micProblem}</Notice>}
        {s.deviceNotice && <Notice>{s.deviceNotice}</Notice>}
        {s.cameraProblem && <Notice tone="warn">{s.cameraProblem}</Notice>}
        {s.shareProblem && <Notice tone="warn">{s.shareProblem}</Notice>}
        {full && watching ? (
          // The shared screen as the stage, everyone beside it (under it in a tall window).
          <div className="patch-stage">
            <ScreenView identity={watching.id} name={watching.name} big />
            <StageStrip s={s} />
          </div>
        ) : (
          <>
            {watching && <ScreenView identity={watching.id} name={watching.name} />}
            {(video || full) && <Tiles s={s} full={full} />}
          </>
        )}
        {s.screens.length > 1 && (
          <div className="patch-screens" role="group" aria-label="Whose screen to watch">
            {s.screens.map((x) => (
              <Button key={x.id} size="small" variant={x.id === s.watching ? 'secondary' : 'ghost'} aria-pressed={x.id === s.watching} onClick={() => watchScreen(x.id)}>
                {x.name}
              </Button>
            ))}
          </div>
        )}
        {s.sharing && (
          <p className="patch-sharing">
            <Icon name="screen" size={16} /> You’re sharing your screen.
          </p>
        )}
        <Declined s={s} />
        <Voicemail s={s} />
        <RingingPhones s={s} />
        <ul className={`patch-people${video || full ? ' sr-only' : ''}`} aria-label="Who’s in">
          {s.people.map((p) => {
            // A desk phone on the Patch (1.12.18): "Dana · on desk phone", "Leaving a voicemail for Dana".
            const desk = deskPhoneOf(s, p.id);
            const name = desk ? desk.text : p.name;
            return (
              <li
                key={p.id}
                className={`patch-person${p.speaking ? ' speaking' : ''}${desk ? ' desk' : ''}`}
                aria-label={`${name}${p.me ? ' (you)' : ''}${p.speaking ? ', speaking' : ''}${p.muted ? ', muted' : ''}${p.sharing ? ', sharing their screen' : ''}`}
              >
                <Face userId={faceOf(p.id)} name={p.name} size="small" className="patch-avatar" />
                <span className="patch-name" aria-hidden="true">
                  {name}
                  {p.me && <span className="patch-you"> (you)</span>}
                </span>
                {p.sharing && (
                  <span className="patch-tag" aria-hidden="true">
                    Sharing
                  </span>
                )}
                <span className={`patch-mic${p.muted ? ' off' : ''}`} aria-hidden="true" title={desk ? 'On a phone' : p.muted ? 'Muted' : 'Microphone on'}>
                  <Icon name={desk ? 'phone' : p.muted ? 'micOff' : 'mic'} size={16} />
                </span>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="patch-controls">
        <MuteButton s={s} />
        <CameraButtons s={s} />
        <DeviceChoices />
        {s.canShare && (
          <Button icon={s.sharing ? 'stop' : 'screen'} disabled={s.phase !== 'connected'} onClick={() => void toggleShare()}>
            {s.sharing ? 'Stop sharing' : 'Share screen'}
          </Button>
        )}
        <Button variant={s.starter ? 'secondary' : 'danger'} icon="logout" onClick={() => void leavePatch()}>
          Leave
        </Button>
        {s.starter && (
          <Button variant="danger" icon="x" onClick={() => void endPatch()}>
            End for everyone
          </Button>
        )}
      </div>
    </section>
  );
}

/**
 * The microphone and, where the browser can send sound to a chosen one, the speaker or headset: kept
 * for Patches and calls on this browser. Each shows when there's more than one to choose from.
 */
function DeviceChoices() {
  const d = useAudioDevices();
  return (
    <>
      {d.mics.length > 1 && (
        <label className="patch-mic-choice">
          <span className="sr-only">Microphone</span>
          <select className="input" value={chosenValue(d.mics, d.mic)} onChange={(e) => chooseAudio('audioinput', e.target.value)} aria-label="Microphone">
            {choices(d.mics).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {d.canChooseSpeaker && d.speakers.length > 1 && (
        <label className="patch-mic-choice patch-speaker-choice">
          <span className="sr-only">Speaker</span>
          <select className="input" value={chosenValue(d.speakers, d.speaker)} onChange={(e) => chooseAudio('audiooutput', e.target.value)} aria-label="Speaker">
            {choices(d.speakers).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
      )}
    </>
  );
}

/**
 * A shared screen's video, attached while it's shown. Attached once per element and track, never
 * again on every change to the Patch (who's speaking changes several times a second): each fresh
 * attach blanked the picture for a moment and made it flicker (Joey, Oct 29 2026).
 */
function useScreenVideo(identity: string): RefObject<HTMLVideoElement | null> {
  const video = useRef<HTMLVideoElement>(null);
  // Heard, so a screen that starts or stops shows here.
  usePatch();
  const track = screenTrack(identity);
  const shown = useRef<{ el: HTMLVideoElement; track: NonNullable<ReturnType<typeof screenTrack>> } | null>(null);
  useEffect(() => {
    const el = video.current;
    const was = shown.current;
    if (was && was.el === el && was.track === track) return;
    if (was) was.track.detach(was.el);
    shown.current = null;
    if (track && el) {
      track.attach(el);
      shown.current = { el, track };
    }
  });
  useEffect(
    () => () => {
      const was = shown.current;
      if (was) was.track.detach(was.el);
      shown.current = null;
    },
    []
  );
  return video;
}

/**
 * Someone's shared screen. Enlarge makes it the stage of a full-screen Patch (double-click does
 * too); Screen only shows just the screen across the whole display (the browser's full screen). On
 * a phone it can be watched (and made large), not shared.
 */
function ScreenView({ identity, name, big }: { identity: string; name: string; big?: boolean }) {
  const figure = useRef<HTMLElement>(null);
  const video = useScreenVideo(identity);
  const fullscreen = useFullscreenElement();
  const only = Boolean(fullscreen && fullscreen === figure.current);
  const enlarge = () => setExpanded(!patchNow().expanded);
  const screenOnly = () => {
    if (only) return exitFullscreen();
    const f = figure.current;
    const v = video.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    // An iPhone can only show the video itself full screen.
    if (f?.requestFullscreen) void f.requestFullscreen().catch(() => v?.webkitEnterFullscreen?.());
    else v?.webkitEnterFullscreen?.();
  };
  const onDoubleClick = () => (only ? exitFullscreen() : enlarge());
  return (
    <figure ref={figure} className={`patch-screen${big ? ' big' : ''}`} aria-label={`${name}’s screen`}>
      <video ref={video} autoPlay playsInline muted aria-label={`${name}’s screen`} title={big ? 'Double-click to make it smaller' : 'Double-click to enlarge'} onDoubleClick={onDoubleClick} />
      <figcaption>
        <span className="patch-screen-name">{name}’s screen</span>
        {!only && (
          <button type="button" className="patch-screen-btn" onClick={enlarge} title={big ? 'Back to the Patch’s own size (Esc)' : 'Make the screen the Patch’s stage, filling the window'}>
            <Icon name={big ? 'shrink' : 'expand'} size={15} />
            {big ? 'Smaller' : 'Enlarge'}
          </button>
        )}
        <button
          type="button"
          className="patch-screen-btn"
          onClick={screenOnly}
          title={only ? 'Back to the Patch (Esc)' : 'Show just the screen, across your whole display'}
        >
          <Icon name={only ? 'shrink' : 'fullscreen'} size={15} />
          {only ? 'Exit screen only' : 'Screen only'}
        </button>
      </figcaption>
    </figure>
  );
}

/** A shared screen on the film strip: the whole screen, not cut to fit. */
function ScreenTile({ identity, name }: { identity: string; name: string }) {
  const video = useScreenVideo(identity);
  return (
    <figure className="patch-tile patch-screen-tile" aria-label={`${name}’s screen`}>
      <video ref={video} autoPlay playsInline muted />
      <figcaption aria-hidden="true">
        <Icon name="screen" size={13} />
        <span>{name}’s screen</span>
      </figcaption>
    </figure>
  );
}

/** The other person who spoke last (they stay first on the strip until someone else speaks). */
function useLastSpeaker(people: PatchPerson[]): string | null {
  const last = useRef<string | null>(null);
  const speaking = people.find((p) => p.speaking && !p.me);
  if (speaking) last.current = speaking.id;
  else if (last.current && !people.some((p) => p.id === last.current)) last.current = null;
  return last.current;
}

function useWindowSize(win: Window): { w: number; h: number } {
  const [size, setSize] = useState(() => ({ w: win.innerWidth, h: win.innerHeight }));
  useEffect(() => {
    const on = () => setSize((was) => (was.w === win.innerWidth && was.h === win.innerHeight ? was : { w: win.innerWidth, h: win.innerHeight }));
    on();
    win.addEventListener('resize', on);
    return () => win.removeEventListener('resize', on);
  }, [win]);
  return size;
}

/**
 * The film strip, like Google Meet's: while someone's video is on and the panel isn't open, the
 * video floats in a corner of the page (drag it, or use its handle's arrow keys, to another corner;
 * kept on this device), with Mute, Open, Pop out and Leave. Whoever's screen is being watched comes
 * first, then whoever spoke last; the rest are "+2". On a narrower window it sits across the bottom
 * where the bar does, and the page makes room for it. `pip`: the same strip in the Patch's own
 * window, filling it.
 */
function FilmStrip({ s, pip }: { s: PatchState; pip?: Window }) {
  const now = useTicking(s.phase === 'connected');
  const box = useRef<HTMLElement>(null);
  const [corner, setCorner] = useState<Corner>(savedCorner);
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const [snap, setSnap] = useState(0);
  const grab = useRef<{ x: number; y: number; id: number; moved: boolean; onTiles: boolean } | null>(null);
  const from = useRef<DOMRect | null>(null);
  const lastSpeaker = useLastSpeaker(s.people);
  const view = useWindowSize(pip ?? window);
  const narrow = useMedia(NARROW);
  const banded = !pip && narrow;
  const movable = !pip && !narrow;
  // As many tiles as fit: the window's height (a corner of the page, or the Patch's own window), or
  // its width across the bottom.
  const max = pip
    ? tilesThatFit(view.h - 96, ((view.w - 16) * 9) / 16, 6, 4)
    : banded
      ? tilesThatFit(view.w - 290, 96, 6, 4)
      : tilesThatFit(view.h - 148 - 96 - 112, (196 * 9) / 16, 6, 3);
  const { tiles, more } = stripTiles({ people: s.people, videos: s.videos, screens: s.screens, watching: s.watching, lastSpeaker, max });
  // Snapping to a corner: from where it was let go, smoothly (unless the person asks for less motion).
  useLayoutEffect(() => {
    const el = box.current;
    const was = from.current;
    from.current = null;
    if (!el || !was || reducedMotion()) return;
    const to = el.getBoundingClientRect();
    if (was.left === to.left && was.top === to.top) return;
    el.style.transition = 'none';
    el.style.transform = `translate(${was.left - to.left}px, ${was.top - to.top}px)`;
    void el.offsetWidth;
    el.style.transition = 'transform 180ms ease-out';
    el.style.transform = '';
    const done = () => (el.style.transition = '');
    el.addEventListener('transitionend', done, { once: true });
  }, [snap]);
  const moveTo = (c: Corner) => {
    from.current = box.current?.getBoundingClientRect() ?? null;
    setDrag(null);
    setCorner(c);
    saveCorner(c);
    setSnap((n) => n + 1);
  };
  // Dragged by any part but its buttons (the handle too); a tap on the video opens the Patch.
  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    const target = e.target as Element;
    if (pip || e.button !== 0 || target.closest('button:not(.patch-strip-grip), a, select')) return;
    grab.current = { x: e.clientX, y: e.clientY, id: e.pointerId, moved: false, onTiles: Boolean(target.closest('.patch-strip-tiles')) };
    if (!movable) return;
    // It follows the pointer straight away, even mid-snap.
    e.currentTarget.style.transition = '';
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const g = grab.current;
    if (!movable || !g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (!g.moved && Math.hypot(dx, dy) < 5) return;
    g.moved = true;
    setDrag({ dx, dy });
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    const g = grab.current;
    grab.current = null;
    if (!g || g.id !== e.pointerId) return;
    if (!g.moved) {
      if (g.onTiles) openPanel(true);
      return;
    }
    const r = box.current?.getBoundingClientRect();
    if (r) moveTo(nearestCorner(r.left + r.width / 2, r.top + r.height / 2, window.innerWidth, window.innerHeight));
  };
  const onPointerCancel = () => {
    grab.current = null;
    setDrag(null);
  };
  const onGripKey = (e: ReactKeyboardEvent) => {
    if (!e.key.startsWith('Arrow')) return;
    e.preventDefault();
    const next = cornerToward(corner, e.key);
    if (next !== corner) moveTo(next);
  };
  const byId = new Map(s.people.map((p) => [p.id, p]));
  const off = s.muted || Boolean(s.micProblem);
  const status = s.phase === 'reconnecting' ? 'Reconnecting…' : s.phase === 'joining' ? 'Joining…' : `${s.title} · ${clockText((now - s.startedAt) / 1000)}`;
  const shownNames = new Set(tiles.map((t) => t.key));
  const rest = [
    ...s.screens.filter((x) => !shownNames.has(`screen:${x.id}`)).map((x) => `${x.name}’s screen`),
    ...s.people.filter((p) => !shownNames.has(`person:${p.id}`) && (!p.me || s.camera)).map((p) => (p.me ? 'you' : p.name))
  ];
  return (
    <section
      ref={box}
      className={`patch-strip${pip ? ' in-pip' : banded ? ' banded' : ''}${drag ? ' dragging' : ''}`}
      data-corner={movable ? corner : undefined}
      aria-label="Patch videos"
      data-private=""
      style={drag ? { transform: `translate(${drag.dx}px, ${drag.dy}px)` } : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <div className="patch-strip-head">
        {movable && (
          <button type="button" className="patch-strip-grip" aria-label="Move the Patch videos" title="Drag to another corner, or use the arrow keys" onKeyDown={onGripKey}>
            <Icon name="grip" size={16} strokeWidth={3} />
          </button>
        )}
        <span className={`patch-dot${s.phase !== 'connected' ? ' waiting' : ''}`} aria-hidden="true" />
        <span className="patch-strip-status">{status}</span>
      </div>
      <div className="patch-strip-tiles">
        {tiles.map((t: StripTile) =>
          t.kind === 'screen' ? (
            <ScreenTile key={t.key} identity={t.id} name={t.name} />
          ) : byId.get(t.id) ? (
            <Tile key={t.key} person={byId.get(t.id)!} video={t.video} mirrored={t.me && s.facing === 'user'} />
          ) : null
        )}
        {more > 0 && (
          <span className="patch-strip-more" title={namesText(rest)}>
            <span aria-hidden="true">+{more}</span>
            <span className="sr-only">and {more} more</span>
          </span>
        )}
      </div>
      <div className="patch-strip-controls">
        <button
          type="button"
          className={`patch-strip-btn${off ? ' on' : ''}`}
          aria-label={off ? 'Unmute' : 'Mute'}
          aria-pressed={off}
          title={off ? 'Unmute' : 'Mute'}
          disabled={(s.phase !== 'connected' && s.phase !== 'reconnecting') || s.micBusy}
          onClick={() => void toggleMute()}
        >
          <Icon name={off ? 'micOff' : 'mic'} size={18} />
        </button>
        {!pip && (
          <button type="button" className="patch-strip-btn" aria-label="Open the Patch" title="Open the Patch" onClick={() => openPanel(true)}>
            <Icon name="expand" size={18} />
          </button>
        )}
        {!pip && pipSupported() && (
          <button
            type="button"
            className="patch-strip-btn"
            aria-label="Pop out"
            title="Pop out: the Patch in a small window of its own, over your other tabs and apps"
            onClick={() => openPip(96 + 154 * Math.min(tiles.length, 3))}
          >
            <Icon name="popOut" size={18} />
          </button>
        )}
        <button type="button" className="patch-strip-btn leave" aria-label="Leave" title={s.starter ? 'Leave: the Patch carries on' : 'Leave the Patch'} onClick={() => void leavePatch()}>
          <Icon name="logout" size={18} />
        </button>
      </div>
    </section>
  );
}

function PatchEnded({ notice }: { notice: string | null }) {
  useEffect(() => {
    const t = window.setTimeout(dismissNotice, 12_000);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div className="patch-bar ended" role="status" data-private="">
      <span className="patch-bar-text">
        <strong>{notice ?? 'The Patch ended.'}</strong>
      </span>
      <Button size="small" variant="ghost" onClick={dismissNotice}>
        Dismiss
      </Button>
    </div>
  );
}

/** A desk phone's own identity in the room (desk-<user id>): their photo is the person's. */
const faceOf = (identity: string) => (identity.startsWith('desk-') ? identity.slice(5) : identity);

/** Desk phones the Patch is ringing (1.12.18): "Ringing Dana’s desk phone", "Calling Dana’s phone". */
function RingingPhones({ s }: { s: PatchState }) {
  const list = s.phones.filter((p) => p.state === 'ringing' && !s.people.some((x) => x.id === p.userId));
  if (!list.length) return null;
  return (
    <p className="patch-declined patch-phones" role="status">
      <Icon name="phone" size={14} /> {list.map((p) => `${p.text}…`).join(' ')}
    </p>
  );
}

/** Why a 1-on-1's other person isn't here, before "Leave a voicemail" (1.13.0). */
const notHereText = (o: PatchState['voicemail'][number]) => {
  const who = o.name.trim().split(/\s+/)[0] || o.name;
  switch (o.why) {
    case 'declined':
      return `${who} can’t join right now.`;
    case 'busy':
      return `${who} is on a call.`;
    case 'dnd':
      return `${who} is on do not disturb.`;
    default:
      return `${who} didn’t pick up.`;
  }
};

/** A 1-on-1's other person didn't come in (1.13.0): "Dana didn’t pick up. [Leave a voicemail]". */
function Voicemail({ s }: { s: PatchState }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const offers = s.voicemail.filter((o) => !s.people.some((p) => p.id === o.userId));
  if (!offers.length) return problem ? <Notice tone="warn">{problem}</Notice> : null;
  return (
    <>
      {offers.map((o) => (
        <div key={o.userId} className="patch-declined patch-voicemail" role="status">
          <span>{notHereText(o)}</span>
          <Button
            size="small"
            variant="secondary"
            disabled={busy !== null}
            onClick={async () => {
              setBusy(o.userId);
              setProblem(await leaveVoicemail(o.userId));
              setBusy(null);
            }}
          >
            <Icon name="voicemail" size={14} /> {busy === o.userId ? 'Calling…' : 'Leave a voicemail'}
          </Button>
        </div>
      ))}
      {problem && <Notice tone="warn">{problem}</Notice>}
    </>
  );
}

/** Someone the Patch rang said no (and hasn't come in since): "Sam can't join right now." */
function Declined({ s }: { s: PatchState }) {
  // Someone with a voicemail offer is said once, by the offer.
  const names = s.declined.filter((x) => !s.people.some((p) => p.id === x.id) && !s.voicemail.some((o) => o.userId === x.id)).map((x) => x.name);
  if (!names.length) return null;
  return (
    <p className="patch-declined" role="status">
      {namesText(names)} can’t join right now.
    </p>
  );
}
